/** The staff member's own invitation: they choose their PIN here. */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { BigButton, Field, Screen, Title } from '../../components/loyalty/ui';

const API = typeof window !== 'undefined' ? window.location.origin : '';

export default function LoyaltyStaffSetup() {
    const [params] = useSearchParams();
    const token = params.get('token') || '';
    const { t } = useTranslation();
    const [row, setRow] = useState(null);
    const [bad, setBad] = useState(false);
    const [pin, setPin] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    useEffect(() => {
        fetch(`${API}/api/loyalty/staff/invite/${token}`)
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then(setRow)
            .catch(() => setBad(true));
    }, [token]);

    const save = async () => {
        setBusy(true);
        setError('');
        try {
            const r = await fetch(`${API}/api/loyalty/staff/set-pin`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, pin }),
            });
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                setError(String(data.detail || 'failed'));
                return;
            }
            setDone(true);
        } finally { setBusy(false); }
    };

    const theme = { ...(row?.design?.theme || {}),
        lux_buttons: !!row?.design?.branding?.lux_buttons };
    if (bad) {
        return <Screen theme={{}} testid="loyalty-staff-setup-bad">
            <p className="p-6 text-center text-sm">
                {t('loyalty.desk.badLink', 'This link is no longer valid. Ask for a new one.')}
            </p>
        </Screen>;
    }
    if (!row) {
        return <Screen theme={{}} testid="loyalty-staff-setup-loading">
            <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin" />
        </Screen>;
    }
    return (
        <Screen theme={theme} branding={row.design?.branding} testid="loyalty-staff-setup">
            <div className="mx-auto w-full max-w-md space-y-4 p-5">
                <Title theme={theme} testid="loyalty-staff-setup-title">
                    {t('loyalty.desk.setPinTitle', 'Choose your PIN')}
                </Title>
                <p className="text-center text-sm opacity-85" data-testid="loyalty-staff-setup-who">
                    {`${row.first_name} · ${row.email}`}
                </p>
                {done ? (
                    <p className="text-center text-sm" data-testid="loyalty-staff-setup-done">
                        {t('loyalty.desk.pinSet',
                            'Your PIN is set. You can sign in on the clinic card now.')}
                    </p>
                ) : (
                    <>
                        <Field theme={theme} label={t('loyalty.pinChoose', 'Choose a 4-digit PIN')} value={pin} type="password"
                               inputMode="numeric" testid="loyalty-staff-setup-pin"
                               onChange={(e) => setPin(e.target.value)} />
                        {error && (
                            <p className="text-center text-sm text-rose-300"
                               data-testid="loyalty-staff-setup-error">
                                {t(`loyalty.desk.error.${error}`, error)}
                            </p>
                        )}
                        <BigButton theme={theme} lux={!!row?.design?.branding?.lux_buttons} disabled={busy || !pin}
                                   testid="loyalty-staff-setup-save" onClick={save}>
                            {busy ? <Loader2 className="h-5 w-5 animate-spin" />
                                : t('common.save', 'Save')}
                        </BigButton>
                    </>
                )}
            </div>
        </Screen>
    );
}
